import { useState, useEffect } from "react";
import { useParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import StatsCard from "../components/dashboard/StatsCard";
import VolumeChart from "../components/dashboard/VolumeChart";
import TenantActivityTable from "../components/dashboard/TenantActivityTable";
import DateRangePickerPopover from "../components/dashboard/DateRangePickerPopover";
import { packageService, PackageStats, MonthlyStats, TenantActivity } from "../services/packageService";
import { getDateRange, DateRangeKey } from "../utils/dateRanges";

export default function VolumeReport() {
  const { id } = useParams<{ id: string }>();
  const buildingId = id ? parseInt(id) : undefined;

  const [stats, setStats] = useState<PackageStats>({ incoming: 0, outgoing: 0, escorts: 0 });
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [tenantActivity, setTenantActivity] = useState<TenantActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRangeKey, setDateRangeKey] = useState<DateRangeKey>("previous_30_days");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!buildingId) return;

      setIsLoading(true);
      setError(null);
      try {
        let startDate: string;
        let endDate: string;

        if (dateRangeKey === "custom" && customStartDate && customEndDate) {
          startDate = customStartDate;
          endDate = customEndDate;
        } else {
          const range = getDateRange(dateRangeKey);
          startDate = range.startDate;
          endDate = range.endDate;
        }

        const report = await packageService.getVolumeReport(buildingId, startDate, endDate);
        setStats(report.totals);
        setMonthlyStats(
          report.series.map((point) => ({
            month: point.period,
            incoming: point.incoming,
            outgoing: point.outgoing,
            escorts: point.escorts,
          })),
        );
        setTenantActivity(report.activity);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [buildingId, dateRangeKey, customStartDate, customEndDate]);

  function handleCustomApply(start: string, end: string) {
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateRangeKey("custom");
  }

  return (
    <>
      <PageMeta
        title="Volume Report | SBM Package Admin"
        description="Package volume report dashboard"
      />

      <div className="min-w-0 max-w-full space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Header with filters */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            Volume Report
          </h1>
          <div className="flex flex-wrap gap-3">
            <DateRangePickerPopover
              value={dateRangeKey}
              onSelect={setDateRangeKey}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomApply={handleCustomApply}
            />
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
              <span className="text-sm text-gray-500 dark:text-gray-400">T</span>
              <select
                className="border-0 bg-transparent text-sm font-medium text-gray-700 focus:outline-none focus:ring-0 dark:text-gray-300"
              >
                <option value="">Tenant</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-6">
          <StatsCard
            value={isLoading ? 0 : stats.incoming}
            label="Incoming"
            color="purple"
          />
          <StatsCard
            value={isLoading ? 0 : stats.outgoing}
            label="Outgoing"
            color="green"
          />
          <StatsCard
            value={isLoading ? 0 : stats.escorts}
            label="Escorts"
            color="coral"
          />
        </div>

        {/* Chart */}
        <VolumeChart data={monthlyStats} />

        {/* Tenant Activity Table */}
        <TenantActivityTable data={tenantActivity} isLoading={isLoading} />
      </div>
    </>
  );
}
